import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { useCreateProduct, useUpdateProduct } from "@/api/product.api";
import { ProductDto } from "@/types/api/api-types";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { productFormSchema, type ProductFormData } from "./productFormSchema";
import { ControlledCountryComboboxCountryCode } from "@/components/ui/customs/controlled-combobox-countryCode";
import { useWineColorOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose: () => void;
  product?: ProductDto | null;
};

export const ProductFormModal = ({ open, onClose, product }: Props) => {
  const { t } = useTranslation(["boCatalog", "common"]);
  const wineColorOptions = useWineColorOptions();
  const isEdit = !!product;

  const { mutateAsync: createProduct, isPending: isCreating } = useCreateProduct();
  const { mutateAsync: updateProduct, isPending: isUpdating } = useUpdateProduct();

  const isPending = isCreating || isUpdating;

  const { control, handleSubmit, reset } = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      description: "",
      descriptionPelican: "",
      countryOfOrigin: "",
      harmonizedCode: "",
      color: undefined as unknown as WineColorEnum,
      volume: 0,
    },
  });

  useEffect(() => {
    if (!open) return;
    if (product) {
      reset({
        description: product.description,
        descriptionPelican: product.descriptionPelican,
        countryOfOrigin: product.countryOfOrigin,
        harmonizedCode: product.harmonizedCode,
        color: product.color as WineColorEnum,
        volume: product.volume,
      });
    } else {
      reset({
        description: "",
        descriptionPelican: "",
        countryOfOrigin: "",
        harmonizedCode: "",
        color: undefined as unknown as WineColorEnum,
        volume: 0,
      });
    }
  }, [open, product, reset]);

  const onSubmit = async (data: ProductFormData) => {
    if (isEdit) {
      await updateProduct({ id: product.id, payload: data });
    } else {
      await createProduct(data);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("productFormModal.editTitle") : t("productFormModal.createTitle")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <ControlledUnifiedInput
              control={control}
              name="description"
              label={t("productFormModal.descriptionLabel")}
              wrapperClassName="col-span-2"
            />
            <ControlledUnifiedInput
              control={control}
              name="descriptionPelican"
              label={t("productFormModal.descriptionPelicanLabel")}
              wrapperClassName="col-span-2"
            />
            <ControlledCountryComboboxCountryCode
              control={control}
              name="countryOfOrigin"
              label={t("productFormModal.countryOfOriginLabel")}
            />
            <ControlledUnifiedInput
              control={control}
              name="harmonizedCode"
              label={t("productFormModal.harmonizedCodeLabel")}
            />
            <ControlledSelectV2
              control={control}
              name="color"
              label={t("productFormModal.colorLabel")}
              options={wineColorOptions}
              placeholder=""
              wrapperClassName="col-span-2"
            />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <Loader2 className="animate-spin" />
              ) : isEdit ? (
                t("productFormModal.save")
              ) : (
                t("productFormModal.create")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
